import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import styles from "./LeagueTable.module.css";
import Subscription from "../Subscription/Subscription";
import ResultAPI from "../../data/result";
import MatchesAPI from "../../data/matches";
import LeagueTableAPI from "../../data/leagueTable";
import {
  FaArrowLeft, FaSearch, FaTrophy, FaCalendarAlt, FaFilter, FaInfoCircle, FaLandmark, FaChartBar, FaChartPie, FaClock, FaMapMarkerAlt, FaArrowRight, FaHome, FaUserFriends, FaRedo, FaGamepad, FaUsers, FaSortAmountDown, FaSortAmountUp, FaTable, FaList, FaSort, FaSortAmountDownAlt, FaSortAmountUpAlt, FaUserTag, FaIdBadge, FaCrown, FaMedal, FaAward, FaStar, FaChevronUp, FaChevronDown, FaEquals, FaTimes, FaCalendarDay, FaCalendarWeek, FaCalendar, FaForward, FaHistory, FaEye, FaEyeSlash, FaExclamationTriangle
} from "react-icons/fa";
import { useLeagueTableState } from "../../states/useLeagueTableState";
import { GlobalContext } from "../../constants/Context";
const LeagueTable = () => {
  const { SITE_ID, CLUB_ID } = useContext(GlobalContext);
  const { divisionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // States for League Table

  const {
    leagueTableData,
    setLeagueTableData,
    loading,
    setLoading,
    error,
    setError,
    searchTerm,
    setSearchTerm,
    filteredTeams,
    setFilteredTeams,
    tableName,
    setTableName,
    tableKey,
    setTableKey,
    tableHeadings,
    setTableHeadings,
    statColumnNumbers,
    setStatColumnNumbers,
    retryKey,
    setRetryKey,
    recentResults,
    setRecentResults,
    filteredRecentResults,
    setFilteredRecentResults,
    groupedResults,
    setGroupedResults,
    resultsSearchTerm,
    setResultsSearchTerm,
    resultsLoading,
    setResultsLoading,
    resultsError,
    setResultsError,
    upcomingMatches,
    setUpcomingMatches,
    filteredUpcomingMatches,
    setFilteredUpcomingMatches,
    groupedUpcomingMatches,
    setGroupedUpcomingMatches,
    upcomingSearchTerm,
    setUpcomingSearchTerm,
    matchesLoading,
    setMatchesLoading,
    matchesError,
    setMatchesError,
    activeTab,
    setActiveTab,
  } = useLeagueTableState();

  // State for sorting
  const [sortConfig, setSortConfig] = useState({
    key: 'position',
    direction: 'asc'
  });

  // Get data from location state
  const year = location.state?.year || new Date().getFullYear().toString();
  const competitionName = location.state?.competitionName || "";

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get current season
  const getDisplaySeason = () => {
    return year || new Date().getFullYear().toString();
  };

  // Get date range for recent matches
  const getDateRangeForRecentMatches = () => {
    const today = new Date();
    const endDate = today.toISOString().split('T')[0];

    // Last 90 days ke matches (10 matches ke liye sufficient)
    const pastDate = new Date(today);
    pastDate.setDate(today.getDate() - 90);
    const startDate = pastDate.toISOString().split('T')[0];

    return { startDate, endDate };
  };

  // Get year-based date range for APIs
const getYearDateRange = () => {
  const displayYear = year || new Date().getFullYear().toString();
  return {
    startDate: `${displayYear}-01-01`,
    endDate: `${displayYear}-12-31`
  };
};

// Component ke andar use karein
const seasonDateRange = getYearDateRange();


  // Format match date for display
  const formatMatchDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const [day, month, year] = dateString.split('/');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${day} ${months[parseInt(month) - 1]} ${year}`;
    } catch (err) {
      return dateString;
    }
  };

  // Get full formatted date for grouping
  const getFullFormattedDate = (dateString) => {
    if (!dateString) return "Unknown Date";
    try {
      const [day, month, year] = dateString.split('/');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (err) {
      return dateString;
    }
  };

  // Determine if match is upcoming or past
  const isUpcomingMatch = (matchDateStr) => {
    if (!matchDateStr) return false;

    try {
      const [day, month, year] = matchDateStr.split('/');
      const matchDate = new Date(year, month - 1, day);
      const today = new Date();

      // Reset times for accurate comparison
      matchDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      return matchDate >= today;
    } catch (error) {
      return false;
    }
  };

  // Calculate margin of victory
  const getMatchMargin = (match) => {
    if (!match.innings || match.innings.length < 2) return null;

    const firstInnings = match.innings[0];
    const secondInnings = match.innings[1];

    if (!firstInnings || !secondInnings) return null;

    const firstTeamRuns = parseInt(firstInnings.runs) || 0;
    const secondTeamRuns = parseInt(secondInnings.runs) || 0;
    const firstTeamWickets = parseInt(firstInnings.wickets) || 0;
    const secondTeamWickets = parseInt(secondInnings.wickets) || 0;

    if (firstTeamRuns > secondTeamRuns) {
      return `by ${firstTeamRuns - secondTeamRuns} runs`;
    } else if (secondTeamRuns > firstTeamRuns) {
      return `by ${10 - secondTeamWickets} wickets`; // Assuming 10 wickets total
    }

    return null;
  };

  // Handle successful data load from LeagueTableAPI
  const handleLeagueTableLoaded = useCallback((data) => {
    if (data.leagueData) {
      setLeagueTableData(data.leagueData);
      setTableName(data.displayName);
      setTableKey(data.tableKey);
      setTableHeadings(data.tableHeadings);
      setStatColumnNumbers(data.statColumnNumbers || []);
      // Use the teams list provided by the API wrapper
      setFilteredTeams(data.filteredTeams);
      setError(null);
    } else {
      // This case handles a successful API call but no data in the array
      setError("No league table data found for this division");
      setLeagueTableData(null);
      setFilteredTeams([]);
      setStatColumnNumbers([]);
    }
  }, []);

  // Handle loading state from LeagueTableAPI
  const handleLeagueTableLoading = useCallback((isLoading) => {
    if (isLoading && leagueTableData) {
      return;
    }
    setLoading(isLoading);
  }, [leagueTableData]);

  // Handle error state from LeagueTableAPI
  const handleLeagueTableError = useCallback((errorMessage) => {
    setError(errorMessage);
    setLoading(false);
    setLeagueTableData(null);
    setFilteredTeams([]);
    setStatColumnNumbers([]);
  }, []);

  // Function to trigger re-fetch of the League Table
  const handleRetry = () => {

    setRetryKey(prevKey => prevKey + 1);
    setError(null);
    setLoading(true);
  };

  // Get last 10 results only
  const getLast10Results = (results) => {
    if (!results || results.length === 0) return [];

    // Completed matches filter karein
    const completedMatches = results.filter(match =>
      match.result && match.result === "W" && match.innings && match.innings.length >= 2
    );

    // Sort by date (newest first)
    const sortedResults = [...completedMatches]
      .sort((a, b) => {
        try {
          const dateA = a.match_date ? new Date(a.match_date.split('/').reverse().join('-')) : new Date(0);
          const dateB = b.match_date ? new Date(b.match_date.split('/').reverse().join('-')) : new Date(0);
          return dateB - dateA;
        } catch (err) {
          return 0;
        }
      });

    // Sirf first 10 results return karein
    return sortedResults.slice(0, 10);
  };

  // Get next 10 matches only
  const getNext10Matches = (matches) => {
    if (!matches || matches.length === 0) return [];

    // Upcoming matches filter karein
    const upcoming = matches.filter(match =>
      isUpcomingMatch(match.match_date)
    );

    // Sort by date (earliest first)
    const sortedUpcoming = [...upcoming]
      .sort((a, b) => {
        try {
          const dateA = a.match_date ? new Date(a.match_date.split('/').reverse().join('-')) : new Date(0);
          const dateB = b.match_date ? new Date(b.match_date.split('/').reverse().join('-')) : new Date(0);
          return dateA - dateB;
        } catch (err) {
          return 0;
        }
      });

    // Sirf first 10 matches return karein
    return sortedUpcoming.slice(0, 10);
  };

  // Group last 10 results by date
  const groupLast10ResultsByDate = (results) => {
    const grouped = {};

    results.forEach(match => {
      const dateStr = getFullFormattedDate(match.match_date);
      if (!grouped[dateStr]) {
        grouped[dateStr] = [];
      }
      grouped[dateStr].push(match);
    });

    // Sort dates chronologically (newest first)
    const sortedGrouped = {};
    Object.keys(grouped)
      .sort((a, b) => {
        try {
          const dateA = new Date(a);
          const dateB = new Date(b);
          return dateB - dateA; // Newest first
        } catch (err) {
          return b.localeCompare(a);
        }
      })
      .forEach(date => {
        // Sort matches within each date by time
        grouped[date].sort((a, b) => {
          const timeA = a.match_time || "23:59";
          const timeB = b.match_time || "23:59";
          return timeA.localeCompare(timeB);
        });
        sortedGrouped[date] = grouped[date];
      });

    return sortedGrouped;
  };

  // Group next 10 matches by date
  const groupNext10MatchesByDate = (matches) => {
    const grouped = {};

    matches.forEach(match => {
      const dateStr = getFullFormattedDate(match.match_date);
      if (!grouped[dateStr]) {
        grouped[dateStr] = [];
      }
      grouped[dateStr].push(match);
    });

    // Sort dates chronologically (earliest first)
    const sortedGrouped = {};
    Object.keys(grouped)
      .sort((a, b) => {
        try {
          const dateA = new Date(a);
          const dateB = new Date(b);
          return dateA - dateB; // Earliest first
        } catch (err) {
          return a.localeCompare(b);
        }
      })
      .forEach(date => {
        // Sort matches within each date by time
        grouped[date].sort((a, b) => {
          const timeA = a.match_time || "00:00";
          const timeB = b.match_time || "00:00";
          return timeA.localeCompare(timeB);
        });
        sortedGrouped[date] = grouped[date];
      });

    return sortedGrouped;
  };

  // Handle results data loaded
// handleResultsLoaded function update karein
const handleResultsLoaded = useCallback((resultsData) => {
  if (!resultsData || resultsData.length === 0) {
    setRecentResults([]);
    setFilteredRecentResults([]);
    setGroupedResults({});
    return;
  }

  // Selected year ke according filter karein
  const filteredBySeason = resultsData.filter(match => {
    if (!match.match_date) return false;
    try {
      const matchYear = match.match_date.split('/')[2];
      return matchYear === year;
    } catch (err) {
      return false;
    }
  });

  // Sirf last 10 results lein
  const last10Results = getLast10Results(filteredBySeason);

  setRecentResults(last10Results);
  setFilteredRecentResults(last10Results);

  // Group results by date
  const grouped = groupLast10ResultsByDate(last10Results);
  setGroupedResults(grouped);

  setResultsError(null);
}, [year]); // year dependency add karein

  // Handle results error
  const handleResultsError = useCallback((errorMsg) => {
    console.error("Results API error:", errorMsg);
    setResultsError(errorMsg);
  }, []);

  // Handle matches data loaded
  const handleMatchesLoaded = useCallback((matchesData) => {
    if (!matchesData || matchesData.length === 0) {
      setUpcomingMatches([]);
      setFilteredUpcomingMatches([]);
      setGroupedUpcomingMatches({});
      return;
    }

    // Sirf next 10 matches lein
    const next10Matches = getNext10Matches(matchesData);

    setUpcomingMatches(next10Matches);
    setFilteredUpcomingMatches(next10Matches);

    // Group upcoming matches by date
    const grouped = groupNext10MatchesByDate(next10Matches);
    setGroupedUpcomingMatches(grouped);

    setMatchesError(null);
  }, []);

  // Handle matches error
  const handleMatchesError = useCallback((errorMsg) => {
    console.error("Matches API error:", errorMsg);
    setMatchesError(errorMsg);
  }, []);

  // Filter teams based on search term
  useEffect(() => {
    if (!leagueTableData || !leagueTableData.values) return;

    if (!searchTerm.trim()) {
      setFilteredTeams(leagueTableData.values);
      return;
    }

    const filtered = leagueTableData.values.filter((team) =>
      team.column_1.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredTeams(filtered);
  }, [searchTerm, leagueTableData]);

  // Filter results based on search term
  useEffect(() => {
    if (!resultsSearchTerm.trim()) {
      setFilteredRecentResults(recentResults);
      const grouped = groupLast10ResultsByDate(recentResults);
      setGroupedResults(grouped);
      return;
    }

    const filtered = recentResults.filter(match => {
      const searchableText = [
        match.home_club_name,
        match.home_team_name,
        match.away_club_name,
        match.away_team_name,
        match.ground_name,
        match.competition_name,
        match.result_description || ''
      ].join(' ').toLowerCase();

      return searchableText.includes(resultsSearchTerm.toLowerCase());
    });

    setFilteredRecentResults(filtered);
    const grouped = groupLast10ResultsByDate(filtered);
    setGroupedResults(grouped);
  }, [resultsSearchTerm, recentResults]);

  // Filter upcoming matches based on search term
  useEffect(() => {
    if (!upcomingSearchTerm.trim()) {
      setFilteredUpcomingMatches(upcomingMatches);
      const grouped = groupNext10MatchesByDate(upcomingMatches);
      setGroupedUpcomingMatches(grouped);
      return;
    }

    const filtered = upcomingMatches.filter(match => {
      const searchableText = [
        match.home_club_name,
        match.home_team_name,
        match.away_club_name,
        match.away_team_name,
        match.ground_name,
        match.competition_name || ''
      ].join(' ').toLowerCase();

      return searchableText.includes(upcomingSearchTerm.toLowerCase());
    });

    setFilteredUpcomingMatches(filtered);
    const grouped = groupNext10MatchesByDate(filtered);
    setGroupedUpcomingMatches(grouped);
  }, [upcomingSearchTerm, upcomingMatches]);

  // Sort teams
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key, direction });

    const sortedTeams = [...filteredTeams].sort((a, b) => {
      if (key === 'position') {
        const aPos = parseInt(a.position) || 0;
        const bPos = parseInt(b.position) || 0;
        return direction === 'asc' ? aPos - bPos : bPos - aPos;
      } else if (key === 'team') {
        const aName = a.column_1 || '';
        const bName = b.column_1 || '';
        return direction === 'asc'
          ? aName.localeCompare(bName)
          : bName.localeCompare(aName);
      } else if (key === 'points') {
        if (!pointsColumnNumber) return 0;
        const aPoints = parseFloat(a[`column_${pointsColumnNumber}`]) || 0;
        const bPoints = parseFloat(b[`column_${pointsColumnNumber}`]) || 0;
        return direction === 'asc' ? aPoints - bPoints : bPoints - aPoints;
      }
      return 0;
    });

    setFilteredTeams(sortedTeams);
  };

  // Helper functions
  const clearSearch = () => {
    setSearchTerm("");
  };

  const clearResultsSearch = () => {
    setResultsSearchTerm("");
  };

  const clearUpcomingSearch = () => {
    setUpcomingSearchTerm("");
  };

  const parseTableKey = () => {
    if (!tableKey) return [];
    return tableKey.split(",&nbsp;").map((item) => item.trim());
  };

  const getColumnHeading = (columnNumber) => {
    return tableHeadings[`column_${columnNumber}`] || `Col ${columnNumber}`;
  };

  const getNumericColumnKeys = (source) =>
    Object.keys(source || {})
      .map((key) => {
        const match = key.match(/^column_(\d+)$/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((num) => Number.isInteger(num) && num >= 2);

  const getStatColumnNumbers = () => {
    const numbers = new Set();
    const addFrom = (source) => {
      getNumericColumnKeys(source).forEach((num) => numbers.add(num));
    };

    addFrom(tableHeadings);
    addFrom(leagueTableData?.headings);
    (leagueTableData?.values || []).forEach(addFrom);
    (filteredTeams || []).forEach(addFrom);

    if (numbers.size === 0) {
      return [];
    }

    const maxColumn = Math.max(...numbers);
    return Array.from({ length: maxColumn - 1 }, (_, index) => index + 2);
  };

  const isPointsHeading = (label) =>
    /^(pts|points)$/i.test(String(label || "").trim());

  const resolvedColumnNumbers =
    statColumnNumbers.length > 0 ? statColumnNumbers : getStatColumnNumbers();
  const pointsColumnNumber =
    resolvedColumnNumbers.find((num) =>
      isPointsHeading(tableHeadings[`column_${num}`])
    ) ??
    resolvedColumnNumbers[resolvedColumnNumbers.length - 1] ??
    null;

  const handleBack = () => {
    navigate(-1);
  };


  // Get promotion/relegation status
  const getPromotionStatus = (position) => {
    const pos = parseInt(position);
    if (pos <= 3) return styles.promotionZone;
    if (pos > filteredTeams.length - 3) return styles.relegationZone;
    return '';
  };

  // Render a single match result
  const renderMatchResult = (match, index) => {
    const {
      match_date,
      match_time,
      home_team_name,
      away_team_name,
      home_club_name,
      away_club_name,
      result_description,
      innings,
      // toss,
      no_of_overs,
      ground_name,
      id
    } = match;

    // Find which team batted first
    const battingFirstId = match.batted_first;
    const firstInnings = innings?.find(inn => inn.team_batting_id === battingFirstId);
    const secondInnings = innings?.find(inn => inn.team_batting_id !== battingFirstId);

    const firstInningsRuns = firstInnings?.runs || "N/A";
    const firstInningsWickets = firstInnings?.wickets || "N/A";
    const firstInningsOvers = firstInnings?.overs || "N/A";

    const secondInningsRuns = secondInnings?.runs || "N/A";
    const secondInningsWickets = secondInnings?.wickets || "N/A";
    const secondInningsOvers = secondInnings?.overs || "N/A";

    // Determine winner
    const isHomeWinner = result_description?.includes(home_club_name);
    const isAwayWinner = result_description?.includes(away_club_name);

    const margin = getMatchMargin(match);

    return (
      <Link to={`/cricket-match/${id}`} style={{ textDecoration: "none" }} key={id || index}>
        <div className={styles.matchCard}>
          <div className={styles.matchHeader}>
            <div className={styles.matchDateContainer}>
              <FaCalendarAlt />
              <span className={styles.matchDate}>
                {formatMatchDate(match_date)}
              </span>
              {match_time && (
                <span className={styles.matchTime}>
                  | {match_time}
                </span>
              )}
            </div>
            <div className={styles.matchFormat}>
              <FaClock />
              <span>{no_of_overs} overs</span>
            </div>
          </div>

          {ground_name && ground_name !== "Add New Ground" && (
            <div className={styles.venueInfo}>
              <FaMapMarkerAlt />
              <span>{ground_name}</span>
            </div>
          )}

          <div className={styles.teamsContainer}>
            {/* Home Team */}
            <div className={`${styles.teamRow} ${isHomeWinner ? styles.winningTeam : ''}`}>
              <div className={styles.teamInfo}>
                <div className={styles.teamNameWrapper}>
                  <span className={styles.teamName}>
                    {home_club_name} - {home_team_name}
                  </span>
                  {isHomeWinner && <span className={styles.winnerBadge}><FaAward /></span>}
                </div>
                <span className={styles.teamRole}>
                  {battingFirstId === match.home_team_id ? "Batting First" : "Bowling First"}
                </span>
              </div>
              <div className={styles.scoreInfo}>
                <span className={styles.score}>
                  {battingFirstId === match.home_team_id ?
                    `${firstInningsRuns}/${firstInningsWickets}` :
                    `${secondInningsRuns}/${secondInningsWickets}`}
                </span>
                <span className={styles.overs}>
                  ({battingFirstId === match.home_team_id ? firstInningsOvers : secondInningsOvers} ov)
                </span>
              </div>
            </div>

            <div className={styles.vsSeparator}>
              <span>VS</span>
            </div>

            {/* Away Team */}
            <div className={`${styles.teamRow} ${isAwayWinner ? styles.winningTeam : ''}`}>
              <div className={styles.teamInfo}>
                <div className={styles.teamNameWrapper}>
                  <span className={styles.teamName}>
                    {away_club_name} - {away_team_name}
                  </span>
                  {isAwayWinner && <span className={styles.winnerBadge}><FaAward /></span>}
                </div>
                <span className={styles.teamRole}>
                  {battingFirstId === match.away_team_id ? "Batting First" : "Bowling First"}
                </span>
              </div>
              <div className={styles.scoreInfo}>
                <span className={styles.score}>
                  {battingFirstId === match.away_team_id ?
                    `${firstInningsRuns}/${firstInningsWickets}` :
                    `${secondInningsRuns}/${secondInningsWickets}`}
                </span>
                <span className={styles.overs}>
                  ({battingFirstId === match.away_team_id ? firstInningsOvers : secondInningsOvers} ov)
                </span>
              </div>
            </div>
          </div>

          <div className={styles.matchFooter}>
            <div className={styles.matchDetails} style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
              {margin && (
                <div className={styles.matchMargin}>
                  <FaChartBar />
                  <span>Won {margin}</span>
                </div>
              )}

              {result_description && (
                <div className={styles.resultInfo}>
                  <FaTrophy />
                  <span>{result_description}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  };

  // Render a single upcoming match
  const renderUpcomingMatch = (match, index) => {
    const {
      match_date,
      match_time,
      home_team_name,
      away_team_name,
      home_club_name,
      away_club_name,
      ground_name,
      competition_name,
      match_type,
      id
    } = match;

    return (
      <div key={id || index} className={`${styles.matchCard} ${styles.upcomingMatch}`}>
        <div className={styles.matchHeader}>
          <div className={styles.matchDateContainer}>
            <FaCalendarAlt />
            <span className={styles.matchDate}>
              {formatMatchDate(match_date)}
            </span>
            {match_time && (
              <span className={styles.matchTime}>
                | {match_time}
              </span>
            )}
          </div>
          <div className={styles.matchFormat}>
            <FaClock />
            <span>{match_type}</span>
          </div>
        </div>

        {ground_name && ground_name !== "Add New Ground" && (
          <div className={styles.venueInfo}>
            <FaMapMarkerAlt />
            <span>{ground_name}</span>
          </div>
        )}

        <div className={styles.teamsContainer}>
          {/* Home Team */}
          <div className={styles.teamRow}>
            <div className={styles.teamInfo}>
              <div className={styles.teamNameWrapper}>
                <span className={styles.teamName}>
                  {home_club_name} - {home_team_name}
                </span>
                <span className={styles.homeBadge}>
                  <FaHome />
                </span>
              </div>
              <span className={styles.teamRole}>
                Home Team
              </span>
            </div>
          </div>

          <div className={styles.vsSeparator}>
            <span>VS</span>
          </div>

          {/* Away Team */}
          <div className={styles.teamRow}>
            <div className={styles.teamInfo}>
              <div className={styles.teamNameWrapper}>
                <span className={styles.teamName}>
                  {away_club_name} - {away_team_name}
                </span>
                <span className={styles.awayBadge}>
                  <FaUserFriends />
                </span>
              </div>
              <span className={styles.teamRole}>
                Away Team
              </span>
            </div>
          </div>
        </div>

        <div className={styles.matchFooter}>
          <div className={styles.matchDetails}>
            <div className={styles.competitionInfo}>
              <FaTrophy />
              <span>{competition_name}</span>
            </div>
            <div className={styles.matchStatus}>
              <FaForward />
              <span>Upcoming Match</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render grouped last 10 results
  const renderGroupedLast10Results = () => {
    const dates = Object.keys(groupedResults);

    if (dates.length === 0) {
      return (
        <div className={styles.noResults}>
          <div className={styles.noResultsIcon}>
            <FaHistory />
          </div>
          <h4>
            {resultsSearchTerm
              ? `No results found matching "${resultsSearchTerm}"`
              : "No recent results available"}
          </h4>
          <p>
            {resultsSearchTerm
              ? `No match results found matching your search`
              : "No recent match results found for this division"}
          </p>
          {resultsSearchTerm && (
            <button
              className={styles.clearSearchButton}
              onClick={clearResultsSearch}
            >
              Clear Search
            </button>
          )}
        </div>
      );
    }

    return (
      <div className={styles.groupedResults}>
        {dates.map(date => (
          <div key={date} className={styles.dateCategory}>
            <div className={styles.categoryHeader}>
              <div className={styles.categoryTitle}>
                <span className={styles.categoryIcon}>
                  <FaCalendarAlt />
                </span>
                <h4 className={styles.categoryName}>
                  {date}
                </h4>
              </div>
            </div>
            <div className={styles.categoryMatches}>
              {groupedResults[date].map((match, index) => renderMatchResult(match, index))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render grouped next 10 matches
  const renderGroupedNext10Matches = () => {
    const dates = Object.keys(groupedUpcomingMatches);

    if (dates.length === 0) {
      return (
        <div className={styles.noUpcoming}>
          <div className={styles.noUpcomingIcon}>
            <FaForward />
          </div>
          <h4>
            {upcomingSearchTerm
              ? `No upcoming matches found matching "${upcomingSearchTerm}"`
              : "No upcoming matches scheduled"}
          </h4>
          <p>
            {upcomingSearchTerm
              ? `No fixtures matching your search for this division`
              : "No future matches found for this division"}
          </p>
          {upcomingSearchTerm && (
            <button
              className={styles.clearSearchButton}
              onClick={clearUpcomingSearch}
            >
              Clear Search
            </button>
          )}
        </div>
      );
    }

    return (
      <div className={styles.groupedUpcoming}>
        {dates.map(date => (
          <div key={date} className={styles.dateCategory}>
            <div className={styles.categoryHeader}>
              <div className={styles.categoryTitle}>
                <span className={styles.categoryIcon}>
                  <FaCalendarAlt />
                </span>
                <h3 className={styles.categoryName}>
                  {date}
                </h3>
              </div>
            </div>
            <div className={styles.categoryMatches}>
              {groupedUpcomingMatches[date].map((match, index) => renderUpcomingMatch(match, index))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {loading && !leagueTableData && (
      <div className={styles.leagueTablePage}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading...</p>
        </div>
      </div>
      )}

      {error && !leagueTableData && (
      <div className={styles.leagueTablePage}>
        <section className={styles.headerSection}>
          <div className={styles.container} width="1400px">
            <div className={styles.headerContent}>
              <div className={styles.teamHeader}>
                <div className={styles.teamTitle}>
                  <h1 className={styles.heroTitle}>
                    Royal Sports League
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className={styles.noDataWrapper}>
          <div className={styles.noData}>
            <div className={styles.noDataIcon}><FaInfoCircle /></div>
            <h3>No Leauge Table Found</h3>
          </div>
        </div>
      </div>
      )}

      {!loading && !error && !leagueTableData && (
      <div className={styles.leagueTablePage}>
        <section className={styles.headerSection}>
          <button className={styles.backButtonHeader} onClick={handleBack}>
            <FaArrowLeft />
            Back
          </button>
          <div className={styles.container} style={{ width: "1400px" }}>
            <div className={styles.headerContent}>

              <div className={styles.teamHeader}>
                <div className={styles.teamLogo}>
                  <FaTrophy />
                </div>
                <div className={styles.teamTitle}>
                  <h1 className={styles.heroTitle}>
                    No Data Available
                  </h1>
                  <p className={styles.clubName}>
                    Division ID: {divisionId}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><FaChartBar /></div>
          <h3>No League Table Data Found</h3>
          <p>Could not load data for Division {divisionId}.</p>
          {year && <p>Requested year: {year}</p>}

          <div className={styles.buttonGroup}>
            <button className={styles.retryButton} onClick={handleRetry}>
              <FaRedo />
              Try Again
            </button>
            <button className={styles.backButton} onClick={handleBack}>
              <FaArrowLeft />
              Back to Competitions
            </button>
          </div>
        </div>
      </div>
      )}

      {leagueTableData && (
    <div className={styles.leagueTablePage}>
      <section className={styles.headerSection}>
        <button className={styles.backButtonHeader} onClick={handleBack}>
          <FaArrowLeft />
          Back
        </button>
        <div className={styles.container} style={{ width: "1400px" }}>
          <div className={styles.headerContent}>

            <div className={styles.teamHeader}>
              <div className={styles.teamLogo}>
                <FaTrophy />
              </div>
              <div className={styles.teamTitle}>
                <h1 className={styles.heroTitle}>
                  {tableName}
                </h1>
                <p className={styles.clubName}>
                  League Table, Results & Fixtures
                </p>
              </div>
            </div>

            <div className={styles.teamMeta}>
              <div className={styles.metaItem}>
                <FaCalendarAlt />
                <span>Season {getDisplaySeason()}</span>
              </div>
              <div className={styles.metaItem}>
                <FaUsers />
                <span>{leagueTableData.values?.length || 0} Teams</span>
              </div>
              <div className={styles.metaItem}>
                <FaChartBar />
                <span>Updated Standings</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Controls Section (Tabs and Search) */}
      <section className={styles.controlsSection}>
        <div className={styles.container}>
          <div className={styles.controlsGrid}>
            {/* Tabs */}
            <div className={styles.tabsContainer}>
              <button
                className={`${styles.tabButton} ${activeTab === "leagueTable" ? styles.activeTab : ""}`}
                onClick={() => setActiveTab("leagueTable")}
              >
                <FaTable />
                League Table ({filteredTeams.length})
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === "results" ? styles.activeTab : ""}`}
                onClick={() => setActiveTab("results")}
              >
                <FaHistory />
                Last 10 Results ({filteredRecentResults.length})
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === "upcoming" ? styles.activeTab : ""}`}
                onClick={() => setActiveTab("upcoming")}
              >
                <FaForward />
                Next 10 Matches ({filteredUpcomingMatches.length})
              </button>
            </div>

            {/* Conditional Search Boxes */}
            {activeTab === "leagueTable" && (
              <div className={styles.searchWrapper}>
                <div className={styles.searchBox}>
                  <FaSearch className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Search teams..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                    aria-label="Search teams"
                  />
                  {searchTerm && (
                    <button
                      className={styles.searchClear}
                      onClick={clearSearch}
                      aria-label="Clear search"
                      type="button"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>
            )}

            {activeTab === "results" && (
              <div className={styles.searchWrapper}>
                <div className={styles.searchBox}>
                  <FaSearch className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Search results by team, ground..."
                    value={resultsSearchTerm}
                    onChange={(e) => setResultsSearchTerm(e.target.value)}
                    className={styles.searchInput}
                    aria-label="Search results"
                  />
                  {resultsSearchTerm && (
                    <button
                      className={styles.searchClear}
                      onClick={clearResultsSearch}
                      aria-label="Clear search"
                      type="button"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>
            )}

            {activeTab === "upcoming" && (
              <div className={styles.searchWrapper}>
                <div className={styles.searchBox}>
                  <FaSearch className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Search fixtures by team, ground..."
                    value={upcomingSearchTerm}
                    onChange={(e) => setUpcomingSearchTerm(e.target.value)}
                    className={styles.searchInput}
                    aria-label="Search upcoming matches"
                  />
                  {upcomingSearchTerm && (
                    <button
                      className={styles.searchClear}
                      onClick={clearUpcomingSearch}
                      aria-label="Clear search"
                      type="button"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className={styles.contentSection}>
        <div className={styles.container}>
          {activeTab === "leagueTable" ? (
            // League Table Tab
            <div className={styles.leagueTableTab}>
              <div className={styles.tableHeader}>
                <h3>
                  <FaTable />
                  League Standings
                  <span className={styles.matchCount}>
                    ({filteredTeams.length} teams)
                  </span>
                </h3>

                <div className={styles.sortControls}>
                  <button
                    className={`${styles.sortButton} ${sortConfig.key === 'position' ? styles.activeSort : ''}`}
                    onClick={() => handleSort('position')}
                  >
                    <FaSort />
                    Position
                    {sortConfig.key === 'position' && (
                      sortConfig.direction === 'asc' ? <FaSortAmountUpAlt /> : <FaSortAmountDownAlt />
                    )}
                  </button>
                  <button
                    className={`${styles.sortButton} ${sortConfig.key === 'team' ? styles.activeSort : ''}`}
                    onClick={() => handleSort('team')}
                  >
                    <FaSort />
                    Team name
                    {sortConfig.key === 'team' && (
                      sortConfig.direction === 'asc' ? <FaSortAmountUpAlt /> : <FaSortAmountDownAlt />
                    )}
                  </button>
                  <button
                    className={`${styles.sortButton} ${sortConfig.key === 'points' ? styles.activeSort : ''}`}
                    onClick={() => handleSort('points')}
                  >
                    <FaSort />
                    Points
                    {sortConfig.key === 'points' && (
                      sortConfig.direction === 'asc' ? <FaSortAmountUpAlt /> : <FaSortAmountDownAlt />
                    )}
                  </button>
                </div>
              </div>

              <div className={styles.tableContainer}>
                {filteredTeams.length === 0 ? (
                  <div className={styles.noMatches}>
                    <div className={styles.noMatchesIcon}>
                      <FaSearch />
                    </div>
                    <h4>No teams found</h4>
                    <p>
                      {searchTerm
                        ? `No teams found matching "${searchTerm}"`
                        : "No team data available for this division"}
                    </p>
                    {searchTerm && (
                      <button
                        className={styles.clearSearchButton}
                        onClick={clearSearch}
                      >
                        Clear Search
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className={styles.tableResponsive}>
                      <table
                        className={styles.leagueTable}
                        style={{
                          minWidth: `${Math.max(
                            560,
                            220 + resolvedColumnNumbers.length * 56
                          )}px`,
                        }}
                      >
                        <thead>
                          <tr>
                            <th className={styles.positionHeader}>
                              <button
                                className={styles.sortableHeader}
                                onClick={() => handleSort('position')}
                              >
                                S.No
                                {sortConfig.key === 'position' && (
                                  sortConfig.direction === 'asc' ? <FaChevronUp /> : <FaChevronDown />
                                )}
                              </button>
                            </th>
                            <th >
                              <button
                                className={styles.sortableHeader}
                                onClick={() => handleSort('team')}
                              >
                                Team
                                {sortConfig.key === 'team' && (
                                  sortConfig.direction === 'asc' ? <FaChevronUp /> : <FaChevronDown />
                                )}
                              </button>
                            </th>
                            {resolvedColumnNumbers.map((colNum) => {
                              const isPoints = pointsColumnNumber === colNum;
                              if (isPoints) {
                                return (
                                  <th
                                    key={`heading-${colNum}`}
                                    className={styles.pointsHeader}
                                  >
                                    <button
                                      className={styles.sortableHeader}
                                      onClick={() => handleSort('points')}
                                    >
                                      {getColumnHeading(colNum)}
                                      {sortConfig.key === 'points' && (
                                        sortConfig.direction === 'asc' ? <FaChevronUp /> : <FaChevronDown />
                                      )}
                                    </button>
                                  </th>
                                );
                              }

                              return (
                                <th key={`heading-${colNum}`} className={styles.statsHeader}>
                                  {getColumnHeading(colNum)}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>

                        <tbody>
                          {filteredTeams.map((team, index) => {
                            return (
                              <tr
                                key={team.team_id || index}
                                className={`${styles.tableRow}`}
                              >
                                <td className={styles.positionCell}>
                                  <div className={styles.positionWrapper}>
                                    <span className={styles.positionBadge}>
                                      {team.position}
                                    </span>
                                  </div>
                                </td>

                                <td className={styles.teamCell} style={{ textAlign: "start" }}>
                                  <div className={styles.teamInfo}>
                                    <div className={styles.teamName}>
                                      <Link
                                        to={`/cricket-team/${team.team_id}`}
                                        className={styles.teamLink}
                                      >
                                        {team.column_1}
                                      </Link>
                                    </div>
                                  </div>
                                </td>

                                {resolvedColumnNumbers.map((colNum) => {
                                  const isPoints = pointsColumnNumber === colNum;
                                  if (isPoints) {
                                    return (
                                      <td key={colNum} className={styles.pointsCell}>
                                        <div className={styles.pointsWrapper}>
                                          <span className={styles.pointsValue}>
                                            {team[`column_${colNum}`]}
                                          </span>
                                        </div>
                                      </td>
                                    );
                                  }

                                  return (
                                    <td key={colNum} className={styles.statsCell}>
                                      {team[`column_${colNum}`]}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              {/* League Summary */}
              <div className={styles.leagueSummary}>
                <div className={styles.summaryCard}>
                  <h4>
                    <FaChartPie />
                    League Summary
                  </h4>
                  <div className={styles.summaryStats}>
                    <div className={styles.statItem}>
                      <span className={styles.statNumber}>
                        {filteredTeams.length}
                      </span>
                      <span className={styles.statLabel}>Total Teams</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statNumber}>
                        3
                      </span>
                      <span className={styles.statLabel}>Promotion Spots</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statNumber}>
                        3
                      </span>
                      <span className={styles.statLabel}>Relegation Spots</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statNumber}>
                        {getDisplaySeason()}
                      </span>
                      <span className={styles.statLabel}>Season</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table Key/Legend */}
              {tableKey && (
                <div className={styles.legendSection}>
                  <div className={styles.legendCard}>
                    <div className={styles.legendHeader}>
                      <h3>
                        <FaInfoCircle />
                        Table Key
                      </h3>
                    </div>

                    <div className={styles.legendBody}>
                      {parseTableKey().map((item, index) => (
                        <div key={index} className={styles.legendItem}>
                          <span className={styles.legendText}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === "results" ? (
            // Recent Results Tab
            <div className={styles.resultsTab}>
              <div className={styles.resultsContainer}>
                <div className={styles.sectionHeader}>
                  <h3>
                    <FaHistory />
                    Recent Match Results
                    <span className={styles.matchCount}>
                      ({filteredRecentResults.length} matches)
                    </span>
                  </h3>
                </div>

                {resultsLoading ? (
                  <div className={styles.loadingResults}>
                    <div className={styles.spinner}></div>
                    <p>Loading recent match results...</p>
                  </div>
                ) : resultsError ? (
                  <div className={styles.errorResults}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>
                      {resultsError.includes("429")
                        ? "Too many requests. Please try again later."
                        : `Error: ${resultsError}`}
                    </p>
                  </div>
                ) : (
                  renderGroupedLast10Results()
                )}

                {filteredRecentResults.length > 0 && (
                  <div className={styles.viewAllContainer}>
                    <button
                      className={styles.viewAllButton}
                      onClick={() => navigate(`/results`)}
                    >
                      <FaList />
                      View All Results
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Upcoming Matches Tab
            <div className={styles.upcomingTab}>
              <div className={styles.upcomingContainer}>
                <div className={styles.sectionHeader}>
                  <h3>
                    <FaForward />
                    Upcoming Matches
                    <span className={styles.matchCount}>
                      ({filteredUpcomingMatches.length} matches)
                    </span>
                  </h3>
                </div>

                {matchesLoading ? (
                  <div className={styles.loadingUpcoming}>
                    <div className={styles.spinner}></div>
                    <p>Loading upcoming matches...</p>
                  </div>
                ) : matchesError ? (
                  <div className={styles.errorUpcoming}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>
                      {matchesError.includes("429")
                        ? "Too many requests. Please try again later."
                        : `Error: ${matchesError}`}
                    </p>
                  </div>
                ) : (
                  renderGroupedNext10Matches()
                )}

                {filteredUpcomingMatches.length > 0 && (
                  <div className={styles.viewAllContainer}>
                    <button
                      className={styles.viewAllButton}
                      onClick={() => navigate(`/cricket-matches`)}
                    >
                      <FaList />
                      View All Fixtures
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <Subscription />

      {/* Existing APIs */}
      <ResultAPI
        siteId={SITE_ID}
        season={getDisplaySeason()}
  fromMatchDate={seasonDateRange.startDate}   
  endMatchDate={seasonDateRange.endDate}  
        divisionId={divisionId}
        onDataLoaded={handleResultsLoaded}
        onError={handleResultsError}
        onLoading={setResultsLoading}
        fetchImmediately={true}
        autoPoll={false}
      />

      <MatchesAPI
        siteId={SITE_ID}
  season={getDisplaySeason()}  
        onDataLoaded={handleMatchesLoaded}
        onError={handleMatchesError}
        onLoading={setMatchesLoading}
      />
    </div>
      )}

      <LeagueTableAPI
        key={divisionId + retryKey}
        divisionId={divisionId}
        competitionName={competitionName}
        season={getDisplaySeason()}
        onDataLoaded={handleLeagueTableLoaded}
        onError={handleLeagueTableError}
        onLoading={handleLeagueTableLoading}
      />
    </>
  );
};

export default LeagueTable;